<template>
  <div class="equipe-por-ordem">
    <!-- Header Simples -->
    <div class="header-simples">
      <div class="titulo-secao">
        <h2>👥 Equipe Ativa</h2>
        <span class="atualizacao" v-if="ultimaAtualizacao">
          Atualizado {{ formatarHoraSimples(ultimaAtualizacao) }}
        </span>
      </div>
      
      <div class="stats-rapidas">
        <div class="stat-item">
          <span class="numero">{{ totalFuncionarios }}</span>
          <span class="label">Funcionários</span>
        </div>
        <div class="stat-item">
          <span class="numero">{{ ordensComEquipe.length }}</span>
          <span class="label">Ordens</span>
        </div>
        <div class="stat-item">
          <span class="numero">{{ setoresAtivos.length }}</span>
          <span class="label">Setores</span>
        </div>
      </div>
      
      <div class="acoes-header">
        <button @click="atualizarDados" :disabled="carregando" class="btn-refresh">
          {{ carregando ? '⏳' : '🔄' }}
        </button>
        <button @click="alternarVisualizacao" class="btn-toggle">
          {{ tipoVisualizacao === 'ordem' ? '🏭 Setores' : '📋 Ordens' }}
        </button>
      </div>
    </div>

    <!-- Busca Simples -->
    <div class="busca-simples" v-if="!carregando && !erro && ordensComEquipe.length > 0">
      <input 
        v-model="filtroTexto" 
        type="text" 
        placeholder="🔍 Buscar funcionário, ordem ou setor..."
        class="input-busca"
      >
    </div>

    <!-- Loading -->
    <div v-if="carregando" class="loading-simples">
      <div class="spinner"></div>
      <p>Carregando equipe...</p>
    </div>

    <!-- Erro -->
    <div v-else-if="erro" class="erro-simples">
      <p>❌ {{ erro }}</p>
      <button @click="carregarDados" class="btn-retry">Tentar Novamente</button>
    </div>

    <!-- Sem Dados -->
    <div v-else-if="ordensComEquipe.length === 0" class="vazio-simples">
      <div class="icone-vazio">😴</div>
      <h3>Nenhuma equipe ativa</h3>
      <p>Não há funcionários trabalhando no momento</p>
    </div>

    <!-- VISUALIZAÇÃO POR ORDEM -->
    <div v-else-if="tipoVisualizacao === 'ordem'" class="lista-ordens">
      <div 
        v-for="ordem in ordensComEquipeFiltradas" 
        :key="ordem.id"
        class="card-ordem"
      >
        <!-- Cabeçalho da Ordem -->
        <div class="ordem-cabecalho">
          <div class="ordem-info">
            <h3 class="ordem-titulo">
              📋 #{{ ordem.id_ordem || ordem.id }}
              <span class="ordem-cliente">{{ ordem.cliente || 'Cliente não informado' }}</span>
            </h3>
            <p class="ordem-produto">{{ obterNomeProduto(ordem) }}</p>
          </div>
          <div class="ordem-status" :class="ordem.status || 'em_andamento'">
            {{ formatarStatusSimples(ordem.status) }}
          </div>
        </div>

        <!-- Lista de Funcionários -->
        <div class="funcionarios-simples" v-if="ordem.funcionarios && ordem.funcionarios.length > 0">
          <div 
            v-for="funcionario in ordem.funcionarios" 
            :key="funcionario.id"
            class="funcionario-item"
            @click="verDetalhesFuncionario(funcionario)"
          >
            <div class="func-avatar">
              {{ obterIniciais(funcionario.nome) }}
            </div>
            <div class="func-info">
              <div class="func-nome">{{ funcionario.nome || 'Nome não informado' }}</div>
              <div class="func-detalhes">
                <span class="setor">🏭 {{ funcionario.setor || 'Setor não informado' }}</span>
                <span class="tempo">⏱️ {{ formatarTempo(funcionario.tempo_trabalhado_minutos) }}</span>
                <span class="inicio">🕐 {{ formatarHora(funcionario.inicio_trabalho) }}</span>
              </div>
            </div>
            <div class="func-actions">
              <button class="btn-ver" title="Ver detalhes" @click.stop="verDetalhesFuncionario(funcionario)">
                👁️
              </button>
            </div>
          </div>
        </div>

        <!-- Caso não tenha funcionários -->
        <div v-else class="sem-funcionarios">
          <p>Nenhum funcionário encontrado para esta ordem</p>
        </div>

        <!-- Resumo da Ordem -->
        <div class="ordem-resumo">
          <span class="resumo-item">
            👥 {{ ordem.funcionarios?.length || 0 }} funcionários
          </span>
          <span class="resumo-item">
            ⏱️ {{ calcularTempoTotalOrdem(ordem) }} total
          </span>
          <button @click="verDetalhesOrdem(ordem)" class="btn-detalhes-ordem">
            Ver Ordem Completa
          </button>
        </div>
      </div>
    </div>

    <!-- VISUALIZAÇÃO POR SETOR -->
    <div v-else class="lista-setores">
      <div 
        v-for="setor in setoresFiltrados" 
        :key="setor.nome"
        class="card-setor"
      >
        <div class="setor-cabecalho">
          <h3>🏭 {{ setor.nome || 'Setor não informado' }}</h3>
          <div class="setor-stats">
            <span>👥 {{ setor.funcionarios?.length || 0 }}</span>
            <span>📋 {{ setor.ordens?.length || 0 }}</span>
          </div>
        </div>
        
        <div class="setor-funcionarios" v-if="setor.funcionarios && setor.funcionarios.length > 0">
          <div 
            v-for="funcionario in setor.funcionarios" 
            :key="funcionario.id"
            class="func-chip"
            @click="verDetalhesFuncionario(funcionario)"
          >
            <span class="chip-avatar">{{ obterIniciais(funcionario.nome) }}</span>
            <div class="chip-info">
              <div class="chip-nome">{{ (funcionario.nome || '').split(' ')[0] || 'N/A' }}</div>
              <div class="chip-tempo">{{ formatarTempo(funcionario.tempo_trabalhado_minutos) }}</div>
            </div>
          </div>
        </div>
        
        <div v-else class="sem-funcionarios-setor">
          <p>Nenhum funcionário ativo neste setor</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { producaoService } from '@/services/producaoService';

export default {
  name: 'EquipePorOrdem',
  emits: ['ver-funcionario', 'ver-ordem'],
  
  data() {
    return {
      ordensComEquipe: [],
      carregando: false,
      erro: null,
      tipoVisualizacao: 'ordem',
      filtroTexto: '',
      ultimaAtualizacao: null
    };
  },
  
  computed: {
    totalFuncionarios() {
      return this.ordensComEquipe.reduce((total, ordem) => {
        return total + (ordem.funcionarios?.length || 0);
      }, 0);
    },
    
    setoresAtivos() {
      const setores = new Set();
      this.ordensComEquipe.forEach(ordem => {
        if (ordem.funcionarios && Array.isArray(ordem.funcionarios)) {
          ordem.funcionarios.forEach(func => {
            if (func.setor) setores.add(func.setor);
          });
        }
      });
      return Array.from(setores).sort();
    },
    
    ordensComEquipeFiltradas() {
      if (!this.filtroTexto.trim()) return this.ordensComEquipe;
      
      const busca = this.filtroTexto.toLowerCase();
      return this.ordensComEquipe.filter(ordem => {
        const textoOrdem = `${ordem.id_ordem || ''} ${ordem.cliente || ''} ${ordem.produto || ''}`.toLowerCase();
        const funcionariosTexto = ordem.funcionarios?.map(f => f.nome || '').join(' ').toLowerCase() || '';
        return textoOrdem.includes(busca) || funcionariosTexto.includes(busca);
      });
    },
    
    setoresFiltrados() {
      const setoresMap = new Map();
      
      this.ordensComEquipeFiltradas.forEach(ordem => {
        if (ordem.funcionarios && Array.isArray(ordem.funcionarios)) {
          ordem.funcionarios.forEach(funcionario => {
            const nomeSetor = funcionario.setor || 'Setor não informado';
            if (!setoresMap.has(nomeSetor)) {
              setoresMap.set(nomeSetor, {
                nome: nomeSetor,
                funcionarios: [],
                ordens: []
              });
            }
            
            const setor = setoresMap.get(nomeSetor);
            if (!setor.funcionarios.find(f => f.id === funcionario.id)) {
              setor.funcionarios.push(funcionario);
            }
            if (!setor.ordens.find(o => o.id === ordem.id)) {
              setor.ordens.push(ordem);
            }
          });
        }
      });
      
      return Array.from(setoresMap.values());
    }
  },
  
  mounted() {
    this.carregarDados();
    this.intervalId = setInterval(() => this.carregarDados(), 30000);
  },
  
  beforeUnmount() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
  
  methods: {
    async carregarDados() {
      try {
        this.carregando = true;
        this.erro = null;
        console.log('👥 Carregando equipe por ordem...');
        
        const response = await producaoService.buscarEquipePorOrdem();
        
        if (response.status === 'sucesso') {
          this.ordensComEquipe = response.ordens || [];
          this.ultimaAtualizacao = new Date();
          console.log('✅ Dados carregados:', this.ordensComEquipe.length, 'ordens com equipe');
        } else {
          throw new Error(response.message || 'Erro ao carregar dados');
        }
      } catch (error) {
        console.error('❌ Erro ao carregar equipe:', error);
        this.erro = error.message || 'Erro ao carregar dados da equipe';
        this.ordensComEquipe = [];
      } finally {
        this.carregando = false;
      }
    },
    
    atualizarDados() {
      this.carregarDados();
    },
    
    alternarVisualizacao() {
      this.tipoVisualizacao = this.tipoVisualizacao === 'ordem' ? 'setor' : 'ordem';
    },
    
    // Utilitários com validação
    formatarHoraSimples(data) {
      if (!data) return '';
      try {
        return new Date(data).toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } catch (error) {
        return '';
      }
    },
    
    formatarStatusSimples(status) {
      const map = {
        'em_andamento': '🔄 Ativo',
        'pendente': '⏸️ Pendente',
        'concluida': '✅ OK'
      };
      return map[status] || '🔄 Ativo';
    },
    
    obterNomeProduto(ordem) {
      return ordem?.produto || ordem?.nome_produto || 'Produto não informado';
    },
    
    obterIniciais(nome) {
      if (!nome || typeof nome !== 'string') return '??';
      
      try {
        return nome.split(' ')
          .filter(p => p.length > 0)
          .slice(0, 2)
          .map(p => p.charAt(0))
          .join('')
          .toUpperCase() || '??';
      } catch (error) {
        return '??';
      }
    },
    
    formatarHora(dataHora) {
      if (!dataHora) return '';
      try {
        return new Date(dataHora).toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } catch (error) {
        return '';
      }
    },
    
    formatarTempo(minutos) {
      if (!minutos || isNaN(minutos)) return '0min';
      const h = Math.floor(minutos / 60);
      const m = minutos % 60;
      return h > 0 ? `${h}h${m}min` : `${m}min`;
    },
    
    calcularTempoTotalOrdem(ordem) {
      if (!ordem.funcionarios || !Array.isArray(ordem.funcionarios)) return '0min';
      
      const total = ordem.funcionarios.reduce((sum, f) => {
        return sum + (f.tempo_trabalhado_minutos || 0);
      }, 0);
      
      return this.formatarTempo(total);
    },
    
    // Eventos
    verDetalhesFuncionario(funcionario) {
      if (funcionario) {
        this.$emit('ver-funcionario', funcionario);
      }
    },
    
    verDetalhesOrdem(ordem) {
      if (ordem) {
        this.$emit('ver-ordem', ordem);
      }
    }
  }
}
</script>

<style scoped>
.equipe-por-ordem {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Simples */
.header-simples {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.titulo-secao h2 {
  margin: 0;
  color: #2c3e50;
}

.atualizacao {
  font-size: 12px;
  color: #7f8c8d;
}

.stats-rapidas {
  display: flex;
  gap: 20px;
}

.stat-item {
  text-align: center;
}

.stat-item .numero {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: #3498db;
}

.stat-item .label {
  font-size: 12px;
  color: #7f8c8d;
}

.acoes-header {
  display: flex;
  gap: 10px;
}

.btn-refresh, .btn-toggle, .btn-retry {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #3498db;
  color: white;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-refresh:hover, .btn-toggle:hover, .btn-retry:hover {
  background: #2980b9;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Busca */
.busca-simples {
  margin-bottom: 20px;
}

.input-busca {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;
}

.input-busca:focus {
  outline: none;
  border-color: #3498db;
}

/* Estados */
.loading-simples, .erro-simples, .vazio-simples {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ecf0f1;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.icone-vazio {
  font-size: 48px;
  margin-bottom: 16px;
}

/* Cards de Ordem */
.lista-ordens {
  display: grid;
  gap: 20px;
}

.card-ordem {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.2s;
}

.card-ordem:hover {
  transform: translateY(-2px);
}

.ordem-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #ecf0f1;
}

.ordem-titulo {
  margin: 0 0 8px 0;
  color: #2c3e50;
}

.ordem-cliente {
  color: #7f8c8d;
  font-weight: normal;
  margin-left: 8px;
}

.ordem-produto {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.ordem-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.ordem-status.em_andamento {
  background: #d4edda;
  color: #155724;
}

.ordem-status.pendente {
  background: #fff3cd;
  color: #856404;
}

.ordem-status.concluida {
  background: #d1ecf1;
  color: #0c5460;
}

/* Funcionários */
.funcionarios-simples {
  padding: 0;
}

.funcionario-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ecf0f1;
  cursor: pointer;
  transition: background 0.2s;
}

.funcionario-item:hover {
  background: #f8f9fa;
}

.funcionario-item:last-child {
  border-bottom: none;
}

.func-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 16px;
  font-size: 14px;
}

.func-info {
  flex: 1;
}

.func-nome {
  font-weight: 500;
  margin-bottom: 4px;
  color: #2c3e50;
}

.func-detalhes {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #7f8c8d;
}

.func-actions {
  margin-left: 16px;
}

.btn-ver {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-ver:hover {
  background: #ecf0f1;
}

/* Sem funcionários */
.sem-funcionarios, .sem-funcionarios-setor {
  padding: 20px;
  text-align: center;
  color: #7f8c8d;
  font-style: italic;
}

/* Resumo da Ordem */
.ordem-resumo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  font-size: 12px;
  color: #7f8c8d;
}

.resumo-item {
  margin-right: 16px;
}

.btn-detalhes-ordem {
  padding: 6px 12px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-detalhes-ordem:hover {
  background: #2980b9;
}

/* Setores */
.lista-setores {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.card-setor {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 20px;
  transition: transform 0.2s;
}

.card-setor:hover {
  transform: translateY(-2px);
}

.setor-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ecf0f1;
}

.setor-cabecalho h3 {
  margin: 0;
  color: #2c3e50;
}

.setor-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #7f8c8d;
}

.setor-funcionarios {
  display: grid;
  gap: 8px;
}

.func-chip {
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.func-chip:hover {
  background: #ecf0f1;
}

.chip-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.chip-info {
  flex: 1;
}

.chip-nome {
  font-weight: 500;
  font-size: 14px;
  color: #2c3e50;
}

.chip-tempo {
  font-size: 12px;
  color: #7f8c8d;
}

/* Responsivo */
@media (max-width: 768px) {
  .header-simples {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .stats-rapidas {
    justify-content: center;
  }
  
  .func-detalhes {
    flex-direction: column;
    gap: 4px;
  }
  
  .ordem-resumo {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .lista-setores {
    grid-template-columns: 1fr;
  }
}
</style>